import { Lock, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import type { Column } from "@/components/shared/data-table";
import { DataTable } from "@/components/shared/data-table";
import { PageHeader } from "@/components/shared/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useHasPermission, useMe } from "@/hooks/use-auth";
import { useRoles } from "@/hooks/use-roles";
import { useServerTable } from "@/hooks/use-server-table";
import { useDeleteUser } from "@/hooks/use-users";
import { useWorkspaces } from "@/hooks/use-workspaces";
import { queryKeys } from "@/lib/query-keys";
import { formatPhone } from "@/lib/utils";
import { userService } from "@/services/user";

import { UserDialog } from "./user-dialog";

import type { UserAccount } from "@artisancode/api-types";

/** `scoped` = the per-workspace page: only this store's users, limited roles. */
export function UserList({ scoped = false }: { scoped?: boolean }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<UserAccount | null>(null);
  const { data: rolesData } = useRoles();
  const roles = rolesData?.items ?? [];
  const { data: workspacesData } = useWorkspaces({ per_page: 100 });
  const workspaces = workspacesData?.items ?? [];
  const { data: me } = useMe();
  const { mutate: deleteUser, isPending: isDeleting } = useDeleteUser();
  const [deleting, setDeleting] = useState<UserAccount | null>(null);
  const canCreate = useHasPermission("users.create");
  const canUpdate = useHasPermission("users.update");
  const canDelete = useHasPermission("users.delete");

  const scopeWorkspaceId = scoped ? (me?.activeWorkspaceId ?? "") : undefined;

  const table = useServerTable<UserAccount>({
    queryKey: (params) =>
      queryKeys.users.list({ ...params, workspace_id: scopeWorkspaceId }),
    fetcher: (params) =>
      userService.list({ ...params, workspace_id: scopeWorkspaceId }),
    pageSize: 10,
  });

  function roleNames(roleIds: string[]) {
    const names = roleIds
      .map((id) => roles.find((r) => r.id === id)?.name)
      .filter(Boolean);
    return names.length > 0 ? names.join(", ") : "-";
  }

  function workspaceName(workspaceId: string) {
    return workspaces.find((w) => w.id === workspaceId)?.name ?? "-";
  }

  // Users holding cross-workspace roles (Developer, Franchisor)
  // are managed from the global page only.
  function isLockedInScope(user: UserAccount) {
    return (
      scoped &&
      user.roleIds.some((id) => {
        const role = roles.find((r) => r.id === id);
        return Boolean(role?.canSwitchWorkspace);
      })
    );
  }

  function openAdd() {
    setEditing(null);
    setOpen(true);
  }

  function openEdit(user: UserAccount) {
    setEditing(user);
    setOpen(true);
  }

  function handleDelete(user: UserAccount) {
    setDeleting(user);
  }

  function confirmDelete() {
    if (!deleting) return;
    deleteUser(deleting.id, {
      onSuccess: () => toast.success("Pengguna berhasil dihapus."),
      onError: () => toast.error("Gagal menghapus pengguna."),
      onSettled: () => setDeleting(null),
    });
  }

  const columns: Column<UserAccount>[] = [
    {
      key: "name",
      label: "Nama",
      render: (u) => (
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium">{u.name}</span>
            {u.isProtected && (
              <Badge variant="outline" className="gap-1 text-[10px]">
                <Lock className="h-3 w-3" />
                Terlindungi
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {u.email}
            {u.phone && (
              <>
                <span className="mx-1.5">•</span>+{u.countryCode || "62"}{" "}
                {formatPhone(u.phone, u.countryCode)}
              </>
            )}
          </p>
        </div>
      ),
    },

    {
      key: "role",
      label: "Role",
      render: (u) => <Badge variant="outline">{roleNames(u.roleIds)}</Badge>,
    },
    ...(me?.canSwitchWorkspace && !scoped
      ? [
          {
            key: "workspace",
            label: "Workspace",
            render: (u: UserAccount) => (
              <span className="text-sm">{workspaceName(u.workspaceId)}</span>
            ),
          },
        ]
      : []),
    {
      key: "status",
      label: "Status",
      render: (u) => (
        <Badge variant={u.status === "active" ? "default" : "outline"}>
          {u.status === "active" ? "Aktif" : "Nonaktif"}
        </Badge>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title={scoped ? "Pengguna & Staf" : "Pengguna"}
        description={
          scoped
            ? "Kelola pemilik, operasional, dan keuangan toko ini."
            : "Kelola pengguna yang dapat mengakses sistem."
        }
        action={
          <Button size="sm" disabled={!canCreate} onClick={openAdd}>
            <Plus className="mr-1 h-4 w-4" />
            Tambah Pengguna
          </Button>
        }
      />
      <DataTable
        data={table.items}
        loadedData={table.loadedItems}
        columns={columns}
        loading={table.loading}
        searchPlaceholder="Cari pengguna..."
        query={table.query}
        onQueryChange={table.onQueryChange}
        page={table.page}
        totalPages={table.totalPages}
        totalCount={table.totalCount}
        onPageChange={table.onPageChange}
        hasMore={table.hasMore}
        onLoadMore={table.onLoadMore}
        actions={(user) => (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              disabled={!canUpdate || isLockedInScope(user)}
              onClick={() => openEdit(user)}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              disabled={
                user.isProtected ||
                user.id === me?.id ||
                !canDelete ||
                isLockedInScope(user)
              }
              onClick={() => handleDelete(user)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      />

      <UserDialog
        open={open}
        onOpenChange={setOpen}
        editing={editing}
        scoped={scoped}
      />
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(next) => !next && setDeleting(null)}
        title="Hapus pengguna?"
        description={
          deleting
            ? `Pengguna "${deleting.name}" akan dihapus dan tidak bisa dikembalikan.`
            : undefined
        }
        onConfirm={confirmDelete}
        isConfirming={isDeleting}
      />
    </div>
  );
}
